import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useAuthDialog } from '../contexts/AuthDialogContext';

const AuthDialog = () => {
  const { open, mode, setMode, closeDialog } = useAuthDialog();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [name, setName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const resetForms = () => {
    setLoginEmail('');
    setLoginPassword('');
    setName('');
    setRegEmail('');
    setRegPassword('');
    setConfirmPassword('');
  };

  const onOpenChange = (next) => {
    if (!next) {
      closeDialog();
      resetForms();
    }
  };

  const goDashboard = () => {
    closeDialog();
    resetForms();
    navigate('/my-learning');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const result = await login(loginEmail, loginPassword);
    setSubmitting(false);
    if (result.success) {
      toast.success('Welcome back.');
      goDashboard();
    } else {
      toast.error(result.error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regPassword.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (regPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    const result = await register(regEmail, regPassword, name);
    setSubmitting(false);
    if (result.success) {
      toast.success('Account created. Welcome to Cynopsis.');
      goDashboard();
    } else {
      toast.error(result.error);
    }
  };

  const fieldClass =
    'h-11 bg-[#0A0A0A] border-white/15 text-white placeholder:text-zinc-600 focus-visible:ring-[#C8102E]';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md border-white/10 bg-[#0D0D0D] text-white p-0 gap-0 overflow-hidden sm:rounded-lg"
        data-testid="auth-dialog"
      >
        <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#C8102E] to-transparent" />

        <div className="p-8">
          <DialogHeader className="text-left space-y-2 mb-8">
            <p className="chapter-label !text-left">ACCOUNT</p>
            <DialogTitle className="font-serif text-3xl text-white font-normal">
              {mode === 'login' ? (
                <>
                  Welcome <em className="text-[#C8102E]">back.</em>
                </>
              ) : (
                <>
                  Join <em className="text-[#C8102E]">Cynopsis.</em>
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              {mode === 'login'
                ? 'Sign in to access your studio and course curriculum.'
                : 'Create your account to unlock curriculum and track your progress.'}
            </DialogDescription>
          </DialogHeader>

          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="text-xs uppercase tracking-widest text-zinc-400">
                  Email
                </Label>
                <Input
                  id="login-email"
                  type="email"
                  required
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="you@email.com"
                  className={fieldClass}
                  data-testid="auth-login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password" className="text-xs uppercase tracking-widest text-zinc-400">
                  Password
                </Label>
                <Input
                  id="login-password"
                  type="password"
                  required
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  className={fieldClass}
                  data-testid="auth-login-password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-cine-primary w-full justify-center"
                data-testid="auth-login-submit"
              >
                {submitting ? 'Signing in…' : 'Log in'}
              </button>
              <p className="text-center text-sm text-zinc-500 pt-2">
                New here?{' '}
                <button
                  type="button"
                  onClick={() => setMode('register')}
                  className="text-[#C8102E] hover:text-white transition-colors font-medium"
                  data-testid="auth-switch-to-register"
                >
                  Create an account
                </button>
              </p>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reg-name" className="text-xs uppercase tracking-widest text-zinc-400">
                  Full name
                </Label>
                <Input
                  id="reg-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className={fieldClass}
                  data-testid="auth-register-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-email" className="text-xs uppercase tracking-widest text-zinc-400">
                  Email
                </Label>
                <Input
                  id="reg-email"
                  type="email"
                  required
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  placeholder="you@email.com"
                  className={fieldClass}
                  data-testid="auth-register-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-password" className="text-xs uppercase tracking-widest text-zinc-400">
                  Password
                </Label>
                <Input
                  id="reg-password"
                  type="password"
                  required
                  minLength={8}
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className={fieldClass}
                  data-testid="auth-register-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-confirm" className="text-xs uppercase tracking-widest text-zinc-400">
                  Confirm password
                </Label>
                <Input
                  id="reg-confirm"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                  className={fieldClass}
                  data-testid="auth-register-confirm"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-cine-primary w-full justify-center mt-2"
                data-testid="auth-register-submit"
              >
                {submitting ? 'Creating account…' : 'Create account'}
              </button>
              <p className="text-center text-sm text-zinc-500 pt-2">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-[#C8102E] hover:text-white transition-colors font-medium"
                  data-testid="auth-switch-to-login"
                >
                  Log in
                </button>
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
